// COMP-PROVIDER-QUEUE — فهرست امروز ارائه‌دهنده (US-SCH-062؛ جاسازی در میز کار و «برنامه من»): نوبت‌های روز به ترتیب زمان با وضعیت
// منتظر، حاضر (با دقیقه انتظار)، در حال خدمت، انجام‌شده و بسته؛ «نوبت بعدی» برجسته و شروع یا پایان خدمت از همین فهرست (US-SCH-035).
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type ProviderQueueItem, type ProviderQueueState } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, LoadingState } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatTime, schKeys } from './common';
import { useAppointmentsChanged } from './realtime';

export const COMP_PROVIDER_QUEUE = 'COMP-PROVIDER-QUEUE';

const TONE: Record<ProviderQueueState, 'default' | 'info' | 'success' | 'warning' | 'primary'> = {
  Waiting: 'default',
  Present: 'warning',
  InService: 'primary',
  Done: 'success',
  Closed: 'default',
};

export interface ProviderQueueProps {
  /** تاریخ کسب‌وکاری (پیش‌فرض امروز شعبه در سرور). */
  date?: string | null;
  timeZone?: string;
}

export function ProviderQueue({ date = null, timeZone }: ProviderQueueProps) {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = schKeys.myQueue(session?.tenantId, date);
  const queue = useQuery({
    queryKey: key,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/scheduling/my/queue', { params: { query: { date: date ?? undefined } } })),
    refetchInterval: 60_000,
  });
  useAppointmentsChanged(() => void queryClient.invalidateQueries({ queryKey: key }));
  const refresh = () => void queryClient.invalidateQueries({ queryKey: schKeys.all(session?.tenantId) });
  const start = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/scheduling/appointments/{id}:start', { params: { path: { id } } })),
    onSuccess: refresh,
  });
  const complete = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/scheduling/appointments/{id}:complete', { params: { path: { id } } })),
    onSuccess: refresh,
  });
  const next = queue.data?.nextAppointmentId ?? null;

  const action = (item: ProviderQueueItem) => {
    if (item.state === 'Present' || item.state === 'Waiting') {
      return (
        <Button
          size="small"
          variant={item.appointmentId === next ? 'contained' : 'outlined'}
          disabled={start.isPending}
          onClick={() => start.mutate(item.appointmentId)}
          data-testid="queue-start"
        >
          {t('sch:provider_queue.start')}
        </Button>
      );
    }
    if (item.state === 'InService') {
      return (
        <Button size="small" variant="contained" color="success" disabled={complete.isPending} onClick={() => complete.mutate(item.appointmentId)} data-testid="queue-complete">
          {t('sch:provider_queue.complete')}
        </Button>
      );
    }
    return null;
  };

  return (
    <Card variant="outlined" data-component-id={COMP_PROVIDER_QUEUE}>
      <CardContent>
        <Typography variant="subtitle1" component="h2" gutterBottom>
          {t('sch:provider_queue.title')}
        </Typography>
        {queue.isPending ? (
          <LoadingState rows={3} />
        ) : queue.error ? (
          <ErrorState error={queue.error} compact onRetry={() => void queue.refetch()} />
        ) : (queue.data?.items ?? []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('sch:provider_queue.empty')}
          </Typography>
        ) : (
          <List dense data-testid="provider-queue">
            {(queue.data?.items ?? []).map((item) => (
              <ListItem
                key={item.appointmentId}
                divider
                data-testid="provider-queue-item"
                data-state={item.state}
                data-next={item.appointmentId === next ? 'true' : undefined}
                secondaryAction={action(item)}
                sx={item.appointmentId === next ? { bgcolor: 'action.selected' } : undefined}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={500}>
                        {formatTime(item.start, timeZone)}
                      </Typography>
                      <Typography variant="body2">{item.patientName ?? '—'}</Typography>
                      <Chip size="small" color={TONE[item.state]} label={t(`sch:provider_queue.state.${item.state}`)} />
                      {item.appointmentId === next ? <Chip size="small" variant="outlined" label={t('sch:provider_queue.next')} /> : null}
                    </Stack>
                  }
                  secondary={[
                    item.serviceName ?? t('sch:day.masked_service'),
                    item.roomName,
                    item.state === 'Present' && item.waitingMinutes !== null && item.waitingMinutes !== undefined
                      ? t('sch:provider_queue.waiting', { minutes: faDigits(Number(item.waitingMinutes)) })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                />
              </ListItem>
            ))}
          </List>
        )}
        {start.error || complete.error ? <ErrorState error={start.error ?? complete.error} compact /> : null}
      </CardContent>
    </Card>
  );
}
